import { GlobalProfiles } from "@proof/authoring";
import type { Project } from "@proof/core";
import { Control } from "./components";
import { profilePlacements } from "./ProfileSwitcher";

/**
 * A project's profiles: which global profile types and reusable forms it uses,
 * and where each profile switcher appears on the form. The first profile
 * pre-fills the form; people switch with the dropdown.
 */
export function ProjectProfiles({
  project,
  onChange,
  notify,
  canAssign,
}: {
  project: Project;
  onChange(project: Project): void;
  notify(message: string): void;
  canAssign: boolean;
}) {
  const placements = profilePlacements(project);
  const where = (instanceId: string) =>
    placements.top.includes(instanceId)
      ? "Top of the form"
      : project.groups.find((group) =>
          placements.sections.get(group.id)?.includes(instanceId),
        )?.name || "Top of the form";
  return (
    <div className="project-profiles">
      <section className="panel padded" aria-label="Profile placement">
        <h2>Profiles on this form</h2>
        <p className="muted">
          The first available profile fills the form automatically. With one
          profile type the switcher sits at the top of the form; with several,
          each section gets the switcher for the profiles mapped into it.
        </p>
        {!project.formInstances.length && (
          <p>
            No profiles are mapped yet. Assign a global profile type below, or
            add a reusable form in Forms.
          </p>
        )}
        {project.formInstances.map((instance) => (
          <div className="profile-placement-row" key={instance.id}>
            <div>
              <strong>{instance.label}</strong>
              <small>
                {Object.keys(instance.mapping).length} mapped variables · Shows
                in {where(instance.id)}
              </small>
            </div>
            <Control label={`Where ${instance.label} switcher appears`}>
              <select
                disabled={!canAssign}
                value={instance.placement || "auto"}
                onChange={(event) =>
                  onChange({
                    ...project,
                    formInstances: project.formInstances.map((row) =>
                      row.id === instance.id
                        ? { ...row, placement: event.target.value }
                        : row,
                    ),
                  })
                }
              >
                <option value="auto">Automatic</option>
                <option value="top">Top of the form</option>
                {[...project.groups]
                  .sort((a, b) => a.order - b.order)
                  .map((group) => (
                    <option key={group.id} value={group.id}>
                      Section · {group.name}
                    </option>
                  ))}
              </select>
            </Control>
          </div>
        ))}
      </section>
      <GlobalProfiles
        project={project}
        onChange={onChange}
        notify={notify}
        canAssign={canAssign}
      />
    </div>
  );
}
